import { useState, useEffect } from 'react';
import { api, type DataSource, type DataRefreshStatusResponse } from '../../api/client';
import './DataRefreshPanel.css';

export function DataRefreshPanel() {
  const [sources, setSources] = useState<DataSource[]>([]);
  const [status, setStatus] = useState<DataRefreshStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshMessage, setRefreshMessage] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [sourcesRes, statusRes] = await Promise.all([
        api.getDataSources(),
        api.getDataRefreshStatus(),
      ]);
      setSources(sourcesRes.sources);
      setStatus(statusRes);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setError(null);
      const res = await api.triggerDataRefresh();
      setRefreshMessage(`${res.message} (Job ID: ${res.job_id})`);
      // Reload status after a short delay
      setTimeout(loadData, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger refresh');
      setRefreshMessage(null);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return <div className="refresh-panel">Loading...</div>;
  }

  return (
    <div className="refresh-panel">
      <div className="panel-header">
        <h3>Data Sources</h3>
        <button
          className="btn-refresh"
          onClick={handleRefresh}
          disabled={status?.isRefreshing}
        >
          {status?.isRefreshing ? 'Refreshing...' : 'Refresh Data'}
        </button>
      </div>

      {error && <div className="panel-error">{error}</div>}
      {refreshMessage && <div className="panel-info">{refreshMessage}</div>}

      <div className="sources-list">
        {sources.map((source) => (
          <div
            key={source.id}
            className={`source-item ${source.isStale ? 'stale' : 'fresh'}`}
          >
            <div className="source-info">
              <span className="source-name">{source.name}</span>
              <span className="source-date">
                {formatDate(source.lastUpdated)}
              </span>
            </div>
            <div className="source-status">
              {source.isStale ? (
                <span className="status-stale">{source.staleDays}d old</span>
              ) : (
                <span className="status-fresh">Up to date</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {status?.activeJobs && status.activeJobs.length > 0 && (
        <div className="active-jobs">
          <h4>Active Jobs</h4>
          {status.activeJobs.map((job) => (
            <div key={job.id} className="job-item">
              <span>{job.market}</span>
              <span className="job-status">{job.status}</span>
            </div>
          ))}
        </div>
      )}

      <p className="schedule-info">
        Auto-refresh: Monthly on the 1st at 6am UTC
      </p>
    </div>
  );
}
